import {Component, Vue} from "vue-property-decorator";

/**
 * Satış kanalının SSO kimlik zinciri — CreateBasket ve OrderEdit ORTAK mantığı.
 *
 * DEV-3174: DEV-3199 düzeltmesi yalnızca CreateBasket'e konmuştu; OrderEdit'ten
 * tamamlanan satışta SSO hesabı açılmıyordu (TDT-3447 canlıda bu yoldan yaşandı).
 * Kopyalamak yerine mantık buraya çıkarıldı; CreateBasket'in davranışı BİREBİR
 * korunur (canlıda çalışan tek satış yolu o).
 *
 * İçerik: SSO kayıt/eşleştirme çağrısı (API proxy'si üzerinden K1-K6 matrisi),
 * K2/K2B/K5 çakışma modalı durum+metotları, CRM geri yazma, TCKN/VKN checksum.
 * Onay EKRANLARI bileşenlere aittir (bağlamları farklı); çakışma modalının
 * şablonu ise ortak SsoCakismaDialog bileşenindedir.
 *
 * Kullanan bileşen `cakismaSonrasiDevam()` metodunu tanımlamalıdır: operatör
 * çakışmayı onayladığında satış zinciri oradan kaldığı yerden tekrarlanır.
 */
@Component
export default class SsoSatisKimlikMixin extends Vue {
  saving: boolean = false;               // çift tık / yeniden giriş koruması
  ssoCakisma: any = null;                // SSO 409 gövdesi {conflict, adaylar, message}
  ssoCakismaDialog: boolean = false;
  cakismaOnaylandi: boolean = false;     // K2/K2B/K5 operatör onayı verildi mi
  secilenAdayId: number | null = null;   // K5: operatörün seçtiği aday
  adayLisanslari: any = {};              // user_id -> lisans listesi (API'den)

  // --- TCKN / VKN checksum (P4, client-side) ---
  // Onay ekranı kilidi için; bağlayıcı doğrulama SSO tarafında da yapılır.
  tcknGecerliMi(tc: string): boolean {
    if (!/^[1-9][0-9]{10}$/.test(tc)) return false;
    const d = tc.split("").map(Number);
    const h10 = ((((d[0] + d[2] + d[4] + d[6] + d[8]) * 7) - (d[1] + d[3] + d[5] + d[7])) % 10 + 10) % 10;
    const h11 = d.slice(0, 10).reduce((a, b) => a + b, 0) % 10;
    return d[9] === h10 && d[10] === h11;
  }

  vknGecerliMi(vkn: string): boolean {
    if (!/^[0-9]{10}$/.test(vkn)) return false;
    let toplam = 0;
    for (let i = 0; i < 9; i++) {
      const gecici = (Number(vkn[i]) + (9 - i)) % 10;
      let carpim = (gecici * Math.pow(2, 9 - i)) % 9;
      if (gecici !== 0 && carpim === 0) carpim = 9;
      toplam += carpim;
    }
    const kontrol = toplam % 10 === 0 ? 0 : 10 - (toplam % 10);
    return Number(vkn[9]) === kontrol;
  }

  dalAciklamasi(dal: string | null): string {
    const aciklamalar: any = {
      K1: "TCKN/VKN ve e-posta SSO'da aynı kayıtta — değişiklik gerekmiyor.",
      K2: "Bu TCKN/VKN SSO'da FARKLI bir e-posta ile kayıtlı. Devam ederseniz onayınız istenecek; onaylarsanız o hesabın e-postası satıştaki e-posta ile DEĞİŞECEK.",
      K2B: "Bu e-posta SSO'da FARKLI bir TCKN/VKN ile kayıtlı. Devam ederseniz onayınız istenecek; onaylarsanız o hesabın kimlik numarası satıştaki değer ile DEĞİŞECEK.",
      K3: "SSO kaydında kimlik numarası boş — satıştaki değer SSO'ya yazılacak.",
      K4: "Satış verisinde kimlik numarası yok — SSO'daki değer korunacak.",
      K5: "TCKN/VKN ve e-posta FARKLI SSO kayıtlarına ait. Hangi hesabın kullanılacağını seçmeniz istenecek.",
      K6: "SSO'da kayıt yok — yeni kullanıcı oluşturulacak ve şifre belirleme e-postası gönderilecek.",
    };
    return dal ? (aciklamalar[dal] || "") : "Ön kontrol yapılamadı; kesin karar kayıt sırasında SSO tarafından verilecek.";
  }

  /**
   * SSO kayıt/eşleştirme çağrısı (panel → API proxy → SSO K1-K6 matrisi).
   *
   * Kayıt SSO'ya panelden DOĞRUDAN gitmez: /api/register suite-içi anahtar
   * ister ve tarayıcı bundle'ına konan hiçbir şey sır değildir. Çağrı,
   * validate.admin arkasındaki admin/sso-register proxy'sinden geçer.
   *
   * @return SSO user_id ya da null (çakışma modalı açıldı veya hata gösterildi;
   *         iki durumda da SATIŞ DEVAM ETMEZ).
   */
  async ssoKayit(licence: any): Promise<number | null> {
    licence.onaylandi = this.cakismaOnaylandi;
    licence.secilen_user_id = this.secilenAdayId;
    const operator: any = this.$store.state.user;
    licence.onaylayan = operator ? ((operator.name || "") + " " + (operator.lastname || "")).trim() : "";
    // Hijyen: SSO haritalama/karşılaştırmaları küçük harfle çalışır (mağazada
    // ham değerle arama gerçek bir hata çıkardı); SSO tarafı da normalize
    // ettiği için davranış değişmez, yalnızca hizalanır.
    licence.email = (licence.email || "").toString().trim().toLowerCase();

    try {
      const response: any = await this.$http.post(
          this.$store.state.icwApiUrl + "admin/sso-register",
          {data: JSON.stringify(licence)}
      );
      const userId = response.data && response.data.data ? response.data.data.user_id : null;
      if (!userId) {
        alert("SSO kaydından kullanıcı numarası alınamadı. Satış durduruldu. Yazılım ekibiyle iletişime geçiniz.");
        return null;
      }
      return userId;
    } catch (error) {
      const yanit = (error as any)?.response;
      if (yanit && yanit.status === 409 && yanit.data && yanit.data.conflict) {
        await this.cakismaModaliniAc(yanit.data);
        return null;
      }
      // Hata yutulmaz: sso_user_id'siz tamamlanan satış "Bu hesaba bağlı ürün
      // bulunamadı" vakalarının kaynağıydı. Satış her iki ekranda da DURUR.
      console.error("SSO kullanıcı kaydı hatası:", error);
      const mesaj = (yanit && yanit.data && (yanit.data.error || yanit.data.message))
          || "SSO kullanıcısı oluşturulamadı.";
      alert(mesaj + " Satış durduruldu. Sorun sürerse yazılım ekibiyle iletişime geçiniz.");
      return null;
    }
  }

  /**
   * CRM'de kisi.sso_user_id'yi taze değerle eşitler (yalnızca güncelleme
   * yapan kisiSsoGuncelle ucu; kişi OLUŞTURMAZ). Başarısızlık satışı
   * DURDURMAZ (sipariş payload'ı taze id'yi zaten taşır) ama operatöre
   * gösterilir.
   *
   * @param kimlik CRM eşleşme alanları: {KisiID?, email, tckimlikno, tax_number}
   */
  async crmSsoGeriYaz(ssoUserId: number, kimlik: any) {
    try {
      const govde: any = {
        sso_user_id: String(ssoUserId),
        email: kimlik.email || null,
        tckimlikno: kimlik.tckimlikno ? kimlik.tckimlikno.toString() : null,
        tax_number: kimlik.tax_number ? kimlik.tax_number.toString() : null,
      };
      if (kimlik.KisiID) {
        govde.KisiID = kimlik.KisiID;
      }
      const yanit: any = await this.$http.post(this.$store.state.panelApiUrl + "kisiSsoGuncelle", govde);
      console.log("CRM sso_user_id geri yazma:", yanit.data);
    } catch (error) {
      const yanit = (error as any)?.response;
      console.error("CRM sso_user_id geri yazma hatası:", error);
      alert("Uyarı: CRM'de kişi kaydının SSO bağı güncellenemedi"
          + (yanit && yanit.data && yanit.data.message ? " (" + yanit.data.message + ")" : "")
          + ". Satış devam ediyor; bu müşteriyi yazılım ekibine bildirin.");
    }
  }

  /** SSO 409 çakışmasını (K2/K2B/K5) operatör kararına açar. */
  async cakismaModaliniAc(cakisma: any) {
    this.ssoCakisma = cakisma;
    this.secilenAdayId = null;
    this.adayLisanslari = {};
    this.ssoCakismaDialog = true;

    // K5 kartlarında adayların bağlı ürünleri gösterilir; lisans verisi
    // backend'dedir (SSO 409'unda BİLEREK yok), buradan çekilir.
    if (cakisma.conflict === "K5" && Array.isArray(cakisma.adaylar)) {
      for (const aday of cakisma.adaylar) {
        try {
          const resp: any = await this.$http.get(
              this.$store.state.icwApiUrl + "admin/sso-user-lisanslari/" + aday.user_id);
          this.$set(this.adayLisanslari, aday.user_id, (resp.data && resp.data.lisanslar) || []);
        } catch (e) {
          this.$set(this.adayLisanslari, aday.user_id, null); // null: yüklenemedi
        }
      }
    }
  }

  /** Çakışma modalı: vazgeç → satış durur, hiçbir kayıt değişmez. */
  cakismaVazgec() {
    this.ssoCakismaDialog = false;
    this.ssoCakisma = null;
    this.cakismaOnaylandi = false;
    this.secilenAdayId = null;
  }

  /**
   * Çakışma onayı için SON kimlik kapısı — geçersiz TCKN/VKN ile onay VERİLEMEZ.
   *
   * ═══════════════════════════════════════════════════════════════════════════
   * DEV-3546 / TDT-3531 — NEDEN VAR
   *
   * K2B onayı, SSO hesabının kimlik numarasını satıştaki değerle EZER (modal
   * metni birebir bunu söyler). Canlı vaka: sipariş adresine checksum'dan
   * geçmeyen bir TCKN girilmişti; onaylansaydı 181 girişi olan AKTİF bir
   * hesabın doğru TCKN'si o geçersiz değerle değişecekti.
   *
   * Ön kontroldeki checksum uyarısı yalnızca ONAY EKRANININ butonunu kilitler
   * (onayKilitli). Çakışma modalı AYRI bir yoldur: 409 ile açılır ve butonu
   * yalnızca K5'te seçim şartına bakar. Bu metot o yolun kapısıdır.
   *
   * Kural bilinçli olarak dar değil: geçersiz bir kimlik numarası hiçbir dalda
   * (K2/K2B/K5) meşru değildir, hepsinde durdurulur. Boş kimlik numarası bu
   * kapının konusu DEĞİLDİR (K4 "satışta kimlik yok" dalı zaten SSO'daki
   * değeri korur).
   *
   * Bağlayıcı doğrulama SSO tarafında da yapılır; burası operatörün yanlışlıkla
   * veri bozmasını engelleyen istemci kapısıdır.
   * ═══════════════════════════════════════════════════════════════════════════
   *
   * @return true → onaya devam edilebilir; false → satış durdu, operatöre söylendi.
   */
  cakismaKimlikKapisi(): boolean {
    const kisi: any = (this as any).onayKisi || {};
    const kimlikNo = ((kisi.kimlikNo || "") + "").trim();
    if (!kimlikNo) return true;

    const kurumMu = !!kisi.kurumMu;
    if (kurumMu ? this.vknGecerliMi(kimlikNo) : this.tcknGecerliMi(kimlikNo)) return true;

    alert((kurumMu ? "Vergi kimlik numarası" : "TC kimlik numarası") + " doğrulamadan (checksum) "
      + "geçmiyor: " + kimlikNo + "\n\n"
      + "Onaylanırsa SSO hesabındaki kimlik numarası bu GEÇERSİZ değerle değişebilirdi. "
      + "Satış durduruldu.\n\n"
      + "Siparişin adres bilgisindeki kimlik numarasını düzeltip tekrar deneyin.");
    return false;
  }

  /**
   * Çakışma modalı: onayla → satış zinciri onay bayrağıyla TEKRARLANIR.
   * Zincirin kendisi bileşene aittir (CreateBasket: sepeti oluştur,
   * OrderEdit: satışı tamamla) — cakismaSonrasiDevam() ile devredilir.
   */
  async cakismaOnayla() {
    if (!this.ssoCakisma) return;
    if (this.ssoCakisma.conflict === "K5" && !this.secilenAdayId) return;
    if (!this.cakismaKimlikKapisi()) return;
    this.cakismaOnaylandi = true;
    this.ssoCakismaDialog = false;
    if (this.saving) return;
    this.saving = true;
    try {
      await this.cakismaSonrasiDevam();
    } finally {
      this.saving = false;
    }
  }

  /** Kullanan bileşen tanımlar: onaylı çakışma sonrası satış zincirinin devamı. */
  cakismaSonrasiDevam(): any {
    console.error("cakismaSonrasiDevam() bileşende tanımlanmalı.");
  }
}
